import React, { lazy } from 'react';
import { ChevronLeft, ChevronRight, Lock, Diamond, Sparkles } from 'lucide-react';
import { ScenarioData } from './ScenarioWizard';
interface Step4PreviewProps {
  scenarioData: ScenarioData;
  onNext: () => void;
  onBack: () => void;
  onUpdatePremium: (premium: boolean) => void;
}
export const Step4Preview: React.FC<Step4PreviewProps> = ({
  scenarioData,
  onNext,
  onBack,
  onUpdatePremium
}) => {
  const getScenarioPreviewText = () => {
    const themeTexts: Record<string, string> = {
      lazy_morning: 'The sheets are warm against my skin as I stretch lazily. I can feel your eyes on me as the morning light filters through the curtains...',
      hot_tub: 'Steam rises around me as I sink deeper into the bubbling water. I beckon you closer, my wet skin glistening...',
      club_seduction: 'The music pulses through my body as I move on the dance floor. Our eyes meet across the room, and I give you a look that says everything...',
      naughty_nurse: "I adjust my uniform, making sure the buttons strain just enough. 'The doctor will see you now,' I say with a mischievous smile...",
      beach_day: 'The sun warms my skin as I lie on the towel. I watch you emerge from the water, droplets cascading down your body...',
      office_affair: "I lean against your desk, twirling a pen between my fingers. 'Working late again?' I ask, slowly unbuttoning my blouse..."
    };
    return themeTexts[scenarioData.theme] || 'Your personalized fantasy is waiting to be unlocked...';
  };
  const getScenarioImage = () => {
    const themeImages: Record<string, string> = {
      lazy_morning: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8G5dn5MSW6J82wkHrSYJp1/image.png',
      hot_tub: 'https://uploadthingy.s3.us-west-1.amazonaws.com/idtJRyqBJKS1XFFJF7VLC8/image.png',
      club_seduction: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8G5dn5MSW6J82wkHrSYJp1/image.png',
      naughty_nurse: 'https://uploadthingy.s3.us-west-1.amazonaws.com/idtJRyqBJKS1XFFJF7VLC8/image.png',
      beach_day: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8G5dn5MSW6J82wkHrSYJp1/image.png',
      office_affair: 'https://uploadthingy.s3.us-west-1.amazonaws.com/idtJRyqBJKS1XFFJF7VLC8/image.png'
    };
    return themeImages[scenarioData.theme] || 'https://uploadthingy.s3.us-west-1.amazonaws.com/8G5dn5MSW6J82wkHrSYJp1/image.png';
  };
  return <div className="w-full" data-id="element-22018">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-22019">
        Preview Your Fantasy
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-22020">
        Your personalized scenario is almost ready!
      </p>
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-22021">
        <div className="relative" data-id="element-22022">
          <div className="aspect-[16/9] relative overflow-hidden" data-id="element-22023">
            <img src={getScenarioImage()} alt="Scenario preview" className="w-full h-full object-cover filter blur-md" data-id="element-22024" />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center" data-id="element-22025">
              <div className="p-4 bg-black/70 rounded-lg max-w-md text-center" data-id="element-22026">
                <Lock size={24} className="text-white mx-auto mb-2" data-id="element-22027" />
                <p className="text-white font-medium mb-2" data-id="element-22028">
                  Unlock this personalized scenario
                </p>
                <p className="text-gray-300 text-sm mb-4" data-id="element-22029">
                  Experience your fantasy in high-quality images and immersive
                  text
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-22030">
          <div className="mb-4" data-id="element-22031">
            <h3 className="font-medium text-gray-900 dark:text-white mb-2" data-id="element-22032">
              Scenario Preview
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm italic" data-id="element-22033">
              "{getScenarioPreviewText()}"
            </p>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-22034">
            <div className="flex items-center gap-2 mb-1" data-id="element-22035">
              <span className="font-medium" data-id="element-22036">Theme:</span>
              <span data-id="element-22037">
                {scenarioData.theme.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
            <div className="flex items-center gap-2 mb-1" data-id="element-22038">
              <span className="font-medium" data-id="element-22039">Location:</span>
              <span data-id="element-22040">
                {scenarioData.location.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-22041">
              <span className="font-medium" data-id="element-22042">Tone:</span>
              <span data-id="element-22043">
                {scenarioData.tone.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Pricing Options */}
      <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 dark:from-pink-500/20 dark:to-purple-500/20 rounded-lg border border-pink-200 dark:border-pink-800/30 p-4 mb-6" data-id="element-22044">
        <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2" data-id="element-22045">
          <Sparkles size={18} className="text-pink-500" data-id="element-22046" />
          Unlock Options
        </h3>
        <div className="space-y-3" data-id="element-22047">
          <div className="flex items-center" data-id="element-22048">
            <input type="radio" id="standard" name="pricing" className="mr-3 text-pink-500 focus:ring-pink-500" checked={!scenarioData.premium} onChange={() => onUpdatePremium(false)} data-id="element-22049" />
            <label htmlFor="standard" className="flex-1" data-id="element-22050">
              <div className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-22051">
                Standard Experience
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-22052">
                Basic scenario with standard details
              </div>
            </label>
            <div className="text-sm font-medium text-pink-600 dark:text-pink-400" data-id="element-22053">
              25 💗
            </div>
          </div>
          <div className="flex items-center" data-id="element-22054">
            <input type="radio" id="premium" name="pricing" className="mr-3 text-pink-500 focus:ring-pink-500" checked={scenarioData.premium} onChange={() => onUpdatePremium(true)} data-id="element-22055" />
            <label htmlFor="premium" className="flex-1" data-id="element-22056">
              <div className="font-medium text-gray-900 dark:text-white text-sm flex items-center gap-1" data-id="element-22057">
                Premium Experience
                <Diamond size={14} className="text-pink-500" data-id="element-22058" />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-22059">
                Enhanced scenario with extra details and higher quality visuals
              </div>
            </label>
            <div className="text-sm font-medium text-pink-600 dark:text-pink-400" data-id="element-22060">
              5 💎
            </div>
          </div>
          <div className="flex items-center" data-id="element-22061">
            <input type="radio" id="add_friend" name="pricing" className="mr-3 text-gray-400 focus:ring-pink-500" disabled data-id="element-22062" />
            <label htmlFor="add_friend" className="flex-1 opacity-70" data-id="element-22063">
              <div className="font-medium text-gray-900 dark:text-white text-sm flex items-center gap-1" data-id="element-22064">
                Add a Friend
                <Lock size={12} className="text-gray-500" data-id="element-22065" />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400" data-id="element-22066">
                Add another angel to your scenario
              </div>
            </label>
            <div className="text-sm font-medium text-gray-600 dark:text-gray-400" data-id="element-22067">
              +10 💗
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-between mt-8" data-id="element-22068">
        <button onClick={onBack} className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg flex items-center gap-1 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-22069">
          <ChevronLeft size={16} data-id="element-22070" />
          <span data-id="element-22071">Back</span>
        </button>
        <button onClick={onNext} className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg flex items-center gap-1 hover:from-pink-600 hover:to-purple-600 transition-colors" data-id="element-22072">
          <span data-id="element-22073">Unlock Now</span>
          <ChevronRight size={16} data-id="element-22074" />
        </button>
      </div>
    </div>;
};